/**
 * @license
 * Copyright 2022 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

export enum ComponentCategory {
  /**
   * Composed, ready-made section presets (hero, nav bar, footer, …) —
   * the "Section & block library" (AGL-538). Multi-node subtrees with
   * sensible defaults, as opposed to single primitive elements.
   */
  BLOCKS = 'Sections & Blocks',
  INPUT = 'Input',
  /** Data-capture elements: forms, fields, search (AGL-541). */
  FORMS = 'Forms',
  /** Visual content: images, video, icons (AGL-541). */
  MEDIA = 'Media',
  /** Store widgets: products, cart, checkout companions (AGL-541). */
  COMMERCE = 'Commerce',
  /** Site-member auth: sign-in, sign-up, password recovery (AGL-553). */
  MEMBERS = 'Members',
  SURFACE = 'Surface',
  NAVIGATION = 'Navigation',
  LAYOUT = 'Layout',
  DATA_DISPLAY = 'Data Display',
  TEXT = 'Text',
  UNCATEGORIZED = 'Uncategorized',
  ALL = 'All',
}

/**
 * Display rank of the element drawer/picker categories (AGL-538). Lower
 * ranks first; categories not listed here (plugin-registered strings like
 * "Marketplace" or the per-host "Your components") sort after the ranked
 * ones alphabetically, and Uncategorized/All always sink to the bottom.
 */
/**
 * Element-drawer group holding the host's own reusable components — the
 * console registers one preset per definition under this label. Shared so
 * the drawer's help affordance can find the group (AGL-2167) rather than
 * re-typing the string in a second place.
 */
export const REUSABLE_COMPONENT_CATEGORY = 'Your components'

/**
 * The same group in an email's drawer (AGL-3287): the host's reusable email
 * blocks, which an email offers in place of the page components it cannot
 * render. A label of its own because the email designer's author thinks in
 * blocks — every element that drawer offers is one.
 */
export const REUSABLE_EMAIL_BLOCK_CATEGORY = 'Your email blocks'

export const COMPONENT_CATEGORY_ORDER: readonly string[] = [
  ComponentCategory.BLOCKS,
  ComponentCategory.LAYOUT,
  ComponentCategory.NAVIGATION,
  ComponentCategory.TEXT,
  ComponentCategory.FORMS,
  ComponentCategory.INPUT,
  ComponentCategory.MEDIA,
  ComponentCategory.DATA_DISPLAY,
  ComponentCategory.COMMERCE,
  ComponentCategory.MEMBERS,
  ComponentCategory.SURFACE,
]
